import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Radio,
  RadioGroup,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  Divider,
  Alert,
  Snackbar,
  Chip
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Send as SubmitIcon
} from '@mui/icons-material';
import { FormDefinition, FormField } from '../../types/forms';
import { FormService } from '../../services/formService';

interface FormPreviewProps {
  form: FormDefinition;
  onBack: () => void;
  onEdit: () => void;
}

const FormPreview: React.FC<FormPreviewProps> = ({ form, onBack, onEdit }) => {
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleFieldChange = (fieldId: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
    
    // Clear error when user starts typing
    if (errors[fieldId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      
      // Validate form data
      const validationResult = await FormService.validateFormData(form.id, formData);
      
      if (!validationResult.isValid && validationResult.errors) {
        setErrors(validationResult.errors);
        setSnackbar({ open: true, message: 'Please fix validation errors', severity: 'error' });
        return;
      }

      // Submit form
      const result = await FormService.submitForm({
        formId: form.id,
        submissionData: formData,
        metadata: {
          previewMode: true,
          timestamp: new Date().toISOString()
        }
      });

      setSnackbar({ 
        open: true, 
        message: `Form submitted successfully! ${result.rulesExecuted?.length || 0} rules executed.`, 
        severity: 'success' 
      });
      
      // Reset form
      setFormData({});
      setErrors({});
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbar({ open: true, message: 'Error submitting form', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: FormField) => {
    const value = formData[field.id] || '';
    const fieldErrors = errors[field.id] || [];
    const hasError = fieldErrors.length > 0;

    const commonProps = {
      key: field.id,
      error: hasError,
      helperText: hasError ? fieldErrors.join(', ') : field.helpText,
      required: field.required,
      fullWidth: true
    };

    // Check conditional display
    if (field.conditional) {
      // const conditionField = form.fields.find(f => f.id === field.conditional!.fieldId);
      const conditionValue = formData[field.conditional.fieldId];
      const targetValue = field.conditional.value;
      
      let shouldShow = false;
      switch (field.conditional.operator) {
        case 'equals':
          shouldShow = conditionValue === targetValue;
          break;
        case 'not_equals':
          shouldShow = conditionValue !== targetValue;
          break;
        case 'contains':
          shouldShow = String(conditionValue || '').includes(targetValue);
          break;
        case 'not_contains':
          shouldShow = !String(conditionValue || '').includes(targetValue);
          break;
        case 'greater_than':
          shouldShow = Number(conditionValue || 0) > Number(targetValue);
          break;
        case 'less_than':
          shouldShow = Number(conditionValue || 0) < Number(targetValue);
          break;
      }
      
      if (!shouldShow) return null;
    }

    const gridSize = getGridSize(field.width || 'full');

    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
        return (
          <Grid {...gridSize} key={field.id}>
            <TextField
              {...commonProps}
              label={field.label}
              placeholder={field.placeholder}
              type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
              value={value}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
            />
          </Grid>
        );

      case 'textarea':
        return (
          <Grid {...gridSize} key={field.id}>
            <TextField
              {...commonProps}
              label={field.label}
              placeholder={field.placeholder}
              multiline
              rows={4}
              value={value}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
            />
          </Grid>
        );

      case 'number':
      case 'currency':
        return (
          <Grid {...gridSize} key={field.id}>
            <TextField
              {...commonProps}
              label={field.label}
              placeholder={field.placeholder}
              type="number"
              value={value}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              InputProps={field.type === 'currency' ? { startAdornment: '$' } : undefined}
            />
          </Grid>
        );

      case 'date':
        return (
          <Grid {...gridSize} key={field.id}>
            <TextField
              {...commonProps}
              label={field.label}
              type="date"
              value={value}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        );

      case 'datetime':
        return (
          <Grid {...gridSize} key={field.id}>
            <TextField
              {...commonProps}
              label={field.label}
              type="datetime-local"
              value={value}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        );

      case 'select':
        return (
          <Grid {...gridSize} key={field.id}>
            <FormControl {...commonProps}>
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={value}
                label={field.label}
                onChange={(e) => handleFieldChange(field.id, e.target.value)}
              >
                {field.options?.map((option) => (
                  <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        );

      case 'multiselect':
        return (
          <Grid {...gridSize} key={field.id}>
            <FormControl {...commonProps}>
              <InputLabel>{field.label}</InputLabel>
              <Select
                multiple
                value={value || []}
                label={field.label}
                onChange={(e) => handleFieldChange(field.id, e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((val) => {
                      const option = field.options?.find(opt => opt.value === val);
                      return <Chip key={val} label={option?.label || val} size="small" />;
                    })}
                  </Box>
                )}
              >
                {field.options?.map((option) => (
                  <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                    <Checkbox checked={Array.isArray(value) && value.includes(option.value)} />
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        );

      case 'radio':
        return (
          <Grid {...gridSize} key={field.id}>
            <FormControl component="fieldset" {...commonProps}>
              <FormLabel component="legend">{field.label}</FormLabel>
              <RadioGroup
                value={value}
                onChange={(e) => handleFieldChange(field.id, e.target.value)}
              >
                {field.options?.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    label={option.label}
                    disabled={option.disabled}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
        );

      case 'checkbox':
        return (
          <Grid {...gridSize} key={field.id}>
            <FormControl component="fieldset" {...commonProps}>
              <FormLabel component="legend">{field.label}</FormLabel>
              <FormGroup>
                {field.options?.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={Array.isArray(value) && value.includes(option.value)}
                        onChange={(e) => {
                          const currentValues = Array.isArray(value) ? value : [];
                          const newValues = e.target.checked
                            ? [...currentValues, option.value]
                            : currentValues.filter((v: string) => v !== option.value);
                          handleFieldChange(field.id, newValues);
                        }}
                      />
                    }
                    label={option.label}
                    disabled={option.disabled}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Grid>
        );

      case 'file':
        return (
          <Grid {...gridSize} key={field.id}>
            <TextField
              {...commonProps}
              label={field.label}
              type="file"
              onChange={(e) => {
                const target = e.target as HTMLInputElement;
                if (target.files && target.files[0]) {
                  handleFieldChange(field.id, target.files[0].name);
                }
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        );

      case 'section':
        return (
          <Grid size={12} key={field.id}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              {field.label}
            </Typography>
            {field.helpText && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {field.helpText}
              </Typography>
            )}
          </Grid>
        );

      case 'divider':
        return (
          <Grid size={12} key={field.id}>
            <Divider sx={{ my: 2 }} />
          </Grid>
        );

      default:
        return (
          <Grid {...gridSize} key={field.id}>
            <TextField
              {...commonProps}
              label={field.label}
              placeholder={field.placeholder}
              value={value}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
            />
          </Grid>
        );
    }
  };

  const getGridSize = (width: string) => {
    switch (width) {
      case 'quarter':
        return { xs: 12, sm: 6, md: 3 };
      case 'third':
        return { xs: 12, sm: 6, md: 4 };
      case 'half':
        return { xs: 12, sm: 6 };
      case 'full':
      default:
        return { xs: 12 };
    }
  };

  const sortedFields = [...form.fields].sort((a, b) => a.order - b.order);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onBack} sx={{ mr: 1 }}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            {form.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Preview Mode • Version {form.version}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={onEdit}
          sx={{ mr: 1 }}
        >
          Edit Form
        </Button>
      </Box>

      {form.description && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {form.description}
        </Alert>
      )}

      {/* Form */}
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2.5}>
            {sortedFields.map(field => renderField(field))}
            
            <Grid size={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting || !form.isActive}
                  startIcon={<SubmitIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  {submitting ? 'Submitting...' : 'Submit Form'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Form Info */}
      <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Form Information
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">Category</Typography>
            <Typography variant="body2">{form.category}</Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">Fields</Typography>
            <Typography variant="body2">{form.fields.length}</Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Typography variant="body2">
              <Chip 
                label={form.isActive ? 'Published' : 'Draft'} 
                color={form.isActive ? 'success' : 'default'} 
                size="small" 
              />
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">Updated</Typography>
            <Typography variant="body2">{new Date(form.updatedAt).toLocaleDateString()}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FormPreview;